import ELK, { ElkNode, ElkExtendedEdge } from "elkjs/lib/elk.bundled.js";
import { DecisionTreeContent, DecisionTreeNode } from "@amzn/ask-legal-domain";

export type Position = { x: number; y: number };

/**
 * Decision tree layout and styling configuration.
 * Single source of truth for all node dimensions, spacing, and visual properties.
 * Referenced by: elk-tree-layout (layout), decision-tree-factory (node styles).
 */
export const TREE_LAYOUT_CONFIG = {
    // Node dimensions
    NODE_WIDTH: 200,
    NODE_HEIGHT: 60,
    NODE_MIN_HEIGHT: 20,
    // Node visual styling
    NODE_PADDING: 5,
    NODE_BORDER_WIDTH: 2,
    NODE_BORDER_RADIUS: 5,
    // ELK layout spacing
    VERTICAL_SPACING: 150,
    HORIZONTAL_SPACING: 80
};

// Singleton ELK instance — reusable, stateless
const elk = new ELK();

/**
 * Simple grid fallback layout when ELK fails (cycle detected or computation error).
 * Arranges nodes in a grid so they're at least visible and separated,
 * rather than overlapping at (0,0).
 */
function gridFallback(nodes: DecisionTreeNode[]): Map<string, Position> {
    const positions = new Map<string, Position>();
    const cols = Math.ceil(Math.sqrt(nodes.length));
    nodes.forEach((node, i) => {
        const col = i % cols;
        const row = Math.floor(i / cols);
        positions.set(node.id, {
            x: col * (TREE_LAYOUT_CONFIG.NODE_WIDTH + TREE_LAYOUT_CONFIG.HORIZONTAL_SPACING),
            y: row * (TREE_LAYOUT_CONFIG.NODE_HEIGHT + TREE_LAYOUT_CONFIG.VERTICAL_SPACING)
        });
    });
    return positions;
}

/**
 * Calculate positions for orphan nodes — nodes not reachable from root.
 * Groups orphans into subtrees and lays each subtree out hierarchically,
 * then tiles all subtrees side-by-side below the main tree.
 *
 * Subtree layout uses a simple recursive tree layout (parent centered above children)
 * rather than calling ELK, to avoid async complexity and keep it fast.
 *
 * @param orphanNodes The orphan nodes to position
 * @param mainTreeMaxY The maximum Y position of the main tree (orphans go below)
 * @returns Map of nodeId → { x, y } positions for orphan nodes
 */
export function calculateOrphanPositions(
    orphanNodes: DecisionTreeNode[],
    mainTreeMaxY: number
): Map<string, Position> {
    const positions = new Map<string, Position>();
    if (orphanNodes.length === 0) return positions;

    // Build a quick lookup for orphan nodes
    const orphanMap = new Map<string, DecisionTreeNode>();
    orphanNodes.forEach(n => orphanMap.set(n.id, n));

    // Find orphan-roots: orphan nodes that have NO parent among other orphans
    const hasOrphanParent = new Set<string>();
    for (const node of orphanNodes) {
        for (const child of node.children) {
            if (orphanMap.has(child.nodeId)) {
                hasOrphanParent.add(child.nodeId);
            }
        }
    }

    const orphanRoots = orphanNodes.filter(n => !hasOrphanParent.has(n.id));
    const orphanChildOnly = orphanNodes.filter(n => hasOrphanParent.has(n.id));

    // Group orphans into subtrees via BFS from each orphan-root
    const assigned = new Set<string>();
    const subtrees: DecisionTreeNode[][] = [];

    for (const root of orphanRoots) {
        const subtree: DecisionTreeNode[] = [];
        const queue: string[] = [root.id];
        while (queue.length > 0) {
            const id = queue.shift()!;
            if (assigned.has(id) || !orphanMap.has(id)) continue;
            assigned.add(id);
            subtree.push(orphanMap.get(id)!);
            const node = orphanMap.get(id)!;
            for (const child of node.children) {
                if (orphanMap.has(child.nodeId) && !assigned.has(child.nodeId)) {
                    queue.push(child.nodeId);
                }
            }
        }
        if (subtree.length > 0) subtrees.push(subtree);
    }

    // Any orphan not assigned to a subtree (isolated cycles among orphans, etc.)
    for (const node of orphanChildOnly) {
        if (!assigned.has(node.id)) {
            subtrees.push([node]);
            assigned.add(node.id);
        }
    }

    // If all nodes have saved positions in customized-layout mode, use them
    const allHaveSavedPositions = orphanNodes.every(n => n.position != null);
    if (allHaveSavedPositions) {
        orphanNodes.forEach(n => positions.set(n.id, n.position!));
        return positions;
    }

    // Layout each subtree, then tile them horizontally
    const orphanStartY = mainTreeMaxY + TREE_LAYOUT_CONFIG.VERTICAL_SPACING * 2;
    const GAP_BETWEEN_SUBTREES = TREE_LAYOUT_CONFIG.HORIZONTAL_SPACING * 1.5;
    let cursorX = 0; // Current X offset for tiling subtrees

    for (const subtree of subtrees) {
        if (subtree.length === 1) {
            // Single node: use saved position or place inline
            const node = subtree[0];
            if (node.position) {
                positions.set(node.id, node.position);
            } else {
                positions.set(node.id, { x: cursorX, y: orphanStartY });
            }
            cursorX += TREE_LAYOUT_CONFIG.NODE_WIDTH + GAP_BETWEEN_SUBTREES;
        } else {
            // Multi-node subtree: use simple hierarchical tree layout
            const subtreePositions = layoutSubtree(subtree, orphanMap);
            // Translate subtree to current cursor position
            let subtreeWidth = 0;
            for (const [nodeId, pos] of subtreePositions) {
                const node = orphanMap.get(nodeId)!;
                if (node.position) {
                    positions.set(nodeId, node.position);
                } else {
                    positions.set(nodeId, { x: pos.x + cursorX, y: pos.y + orphanStartY });
                }
                subtreeWidth = Math.max(subtreeWidth, pos.x + TREE_LAYOUT_CONFIG.NODE_WIDTH);
            }
            cursorX += subtreeWidth + GAP_BETWEEN_SUBTREES;
        }
    }

    return positions;
}

/**
 * Simple recursive tree layout for an orphan subtree.
 * Positions the root at top, children below, grandchildren below that, etc.
 * Centers each parent above its children.
 *
 * Returns positions relative to (0, 0) — caller must translate.
 */
function layoutSubtree(
    subtree: DecisionTreeNode[],
    nodeMap: Map<string, DecisionTreeNode>
): Map<string, Position> {
    const positions = new Map<string, Position>();
    const subtreeIds = new Set(subtree.map(n => n.id));

    // Find the root of this subtree (node with no parent in the subtree)
    const hasParentInSubtree = new Set<string>();
    for (const node of subtree) {
        for (const child of node.children) {
            if (subtreeIds.has(child.nodeId)) {
                hasParentInSubtree.add(child.nodeId);
            }
        }
    }
    const subtreeRoot = subtree.find(n => !hasParentInSubtree.has(n.id)) || subtree[0];

    // Calculate width of each node's subtree (leaf = 1, parent = sum of children widths)
    const widthCache = new Map<string, number>();
    const visited = new Set<string>();

    function calcWidth(nodeId: string): number {
        if (widthCache.has(nodeId)) return widthCache.get(nodeId)!;
        if (visited.has(nodeId)) return 1; // cycle guard
        visited.add(nodeId);

        const node = nodeMap.get(nodeId);
        if (!node) { widthCache.set(nodeId, 1); return 1; }

        const subtreeChildren = node.children.filter(c => subtreeIds.has(c.nodeId));
        if (subtreeChildren.length === 0) {
            widthCache.set(nodeId, 1);
            return 1;
        }

        const totalWidth = subtreeChildren.reduce((sum, c) => sum + calcWidth(c.nodeId), 0);
        widthCache.set(nodeId, totalWidth);
        return totalWidth;
    }

    calcWidth(subtreeRoot.id);

    // Assign positions using pre-order DFS
    const cellWidth = TREE_LAYOUT_CONFIG.NODE_WIDTH + TREE_LAYOUT_CONFIG.HORIZONTAL_SPACING;
    const cellHeight = TREE_LAYOUT_CONFIG.NODE_HEIGHT + TREE_LAYOUT_CONFIG.VERTICAL_SPACING;
    const placed = new Set<string>();

    function place(nodeId: string, leftSlot: number, depth: number) {
        if (placed.has(nodeId)) return; // cycle guard
        placed.add(nodeId);

        const node = nodeMap.get(nodeId);
        if (!node) return;

        const myWidth = widthCache.get(nodeId) ?? 1;
        // Center this node over its width
        const centerX = (leftSlot + myWidth / 2) * cellWidth - TREE_LAYOUT_CONFIG.NODE_WIDTH / 2;
        positions.set(nodeId, { x: centerX, y: depth * cellHeight });

        const subtreeChildren = node.children.filter(c => subtreeIds.has(c.nodeId));
        let childSlot = leftSlot;
        for (const child of subtreeChildren) {
            const childWidth = widthCache.get(child.nodeId) ?? 1;
            place(child.nodeId, childSlot, depth + 1);
            childSlot += childWidth;
        }
    }

    place(subtreeRoot.id, 0, 0);

    return positions;
}

/**
 * Detect cycles in the graph using DFS.
 * Returns the list of node IDs forming a cycle, or null if no cycle exists.
 *
 * Uses standard 3-color DFS cycle detection:
 * - WHITE (unvisited) → GRAY (in current DFS path) → BLACK (fully processed)
 * - A back-edge to a GRAY node indicates a cycle.
 */
export function detectCycle(
    rootNodeId: string,
    nodeMap: Map<string, DecisionTreeNode>
): string[] | null {
    const WHITE = 0, GRAY = 1, BLACK = 2;
    const color = new Map<string, number>();
    const parent = new Map<string, string | null>();

    // Initialize all nodes as WHITE
    nodeMap.forEach((_, id) => color.set(id, WHITE));

    function dfs(nodeId: string): string[] | null {
        color.set(nodeId, GRAY);

        const node = nodeMap.get(nodeId);
        if (node?.children) {
            for (const child of node.children) {
                const childId = child.nodeId;
                if (!nodeMap.has(childId)) continue;

                const childColor = color.get(childId);
                if (childColor === GRAY) {
                    // Back-edge found → cycle detected
                    const cycle: string[] = [childId, nodeId];
                    let cur: string | undefined = nodeId;
                    while (cur !== childId) {
                        cur = parent.get(cur);
                        if (cur === undefined) break;
                        cycle.push(cur);
                    }
                    return cycle.reverse();
                }
                if (childColor === WHITE) {
                    parent.set(childId, nodeId);
                    const result = dfs(childId);
                    if (result) return result;
                }
            }
        }

        color.set(nodeId, BLACK);
        return null;
    }

    const rootNode = nodeMap.get(rootNodeId);
    if (rootNode) {
        const result = dfs(rootNodeId);
        if (result) return result;
    }

    return null;
}

/**
 * ELK-based Layered Layout for Decision Trees
 *
 * Uses the industrial-grade Eclipse Layout Kernel (ELK) layered algorithm
 * (full Sugiyama: cycle removal, crossing minimization, Brandes-Köpf coordinates).
 *
 * Natively supports DAG structures (converging paths) — each node appears exactly once.
 * Performs cycle detection before layout; returns empty map on cycle or error.
 *
 * Note: We only use ELK for node positioning. Edge rendering is handled by
 * ReactFlow's getBezierPath, which computes paths from the node positions.
 * ELK's edge routing output is intentionally not used — integrating it would
 * require custom SVG path rendering, adding complexity without significant benefit.
 *
 * @returns Map of nodeId → { x, y } positions
 */
export async function calculateLayout(treeContent: DecisionTreeContent): Promise<Map<string, Position>> {
    const positions = new Map<string, Position>();

    const allNodes = treeContent.allNodes;
    if (allNodes.length === 0) return positions;

    // Build node map for O(1) lookup
    const nodeMap = new Map<string, DecisionTreeNode>();
    allNodes.forEach(node => nodeMap.set(node.id, node));

    const rootNode = nodeMap.get(treeContent.rootNodeId);
    if (!rootNode) return positions;

    // Cycle detection — fall back to grid layout if cycle exists
    const cycle = detectCycle(treeContent.rootNodeId, nodeMap);
    if (cycle) {
        console.error(
            `[ELK Layout] Cycle detected in decision tree, using grid fallback. ` +
            `Cycle path: ${cycle.join(" → ")}.`
        );
        return gridFallback(allNodes);
    }

    // Collect reachable nodes and edges via BFS
    // Each node appears exactly once (visited set prevents duplication)
    const elkChildren: ElkNode[] = [];
    const elkEdges: ElkExtendedEdge[] = [];
    const visited = new Set<string>();
    const queue: DecisionTreeNode[] = [rootNode];

    while (queue.length > 0) {
        const node = queue.shift()!;
        if (visited.has(node.id)) continue;
        visited.add(node.id);

        elkChildren.push({
            id: node.id,
            width: TREE_LAYOUT_CONFIG.NODE_WIDTH,
            height: TREE_LAYOUT_CONFIG.NODE_HEIGHT
        });

        if (node.children) {
            node.children.forEach(child => {
                const childNode = nodeMap.get(child.nodeId);
                if (childNode) {
                    elkEdges.push({
                        id: `${node.id}->${child.nodeId}`,
                        sources: [node.id],
                        targets: [child.nodeId]
                    });
                    if (!visited.has(child.nodeId)) {
                        queue.push(childNode);
                    }
                }
            });
        }
    }

    // Build ELK graph
    const graph: ElkNode = {
        id: "root",
        layoutOptions: {
            "elk.algorithm": "layered",
            "elk.direction": "DOWN",
            "elk.layered.crossingMinimization.strategy": "LAYER_SWEEP",
            "elk.layered.nodePlacement.strategy": "BRANDES_KOEPF",
            "elk.layered.spacing.nodeNodeBetweenLayers": String(TREE_LAYOUT_CONFIG.VERTICAL_SPACING),
            "elk.spacing.nodeNode": String(TREE_LAYOUT_CONFIG.HORIZONTAL_SPACING),
            "elk.layered.considerModelOrder.strategy": "NODES_AND_EDGES"
        },
        children: elkChildren,
        edges: elkEdges
    };

    // Run ELK layout with error handling
    let layoutGraph: ElkNode;
    try {
        layoutGraph = await elk.layout(graph);
    } catch (err) {
        console.error("[ELK Layout] Layout computation failed, using grid fallback:", err);
        return gridFallback(allNodes);
    }

    // Extract positions from ELK result
    if (layoutGraph.children) {
        for (const elkNode of layoutGraph.children) {
            if (elkNode.x !== undefined && elkNode.y !== undefined) {
                positions.set(elkNode.id, { x: elkNode.x, y: elkNode.y });
            }
        }
    }

    return positions;
}
